/**
 * DashboardGridWrapper — universal react-grid-layout canvas that governs
 * every functional feature block across all dashboards and roles.
 *
 * • Responsive breakpoints lg:1200 / md:996 / sm:768 / xs:480 (+xxs:0)
 * • compactType="vertical", rowHeight 40, margin [12,12]
 * • Header drag handles (.widget-drag-handle) — touch scrolling preserved
 * • Per-widget resize (minW/minH), minimize (h→1), delete (→hiddenWidgetIds)
 * • "Manage & Restore Widgets" catalog drawer + GMT+00 clock toolbar
 * • State persisted to localStorage + backend user-preferences API
 *
 * Usage:
 *   <DashboardGridWrapper role="TRADER" viewId="trader-main" widgets={[
 *     { id, title, category, layout:{x,y,w,h}, minW, minH, noPadding, render }
 *   ]} />
 */
import { useEffect, useState, useCallback } from "react";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "./dashboard-grid.css";
import { GripVertical, Minus, Plus, X, LayoutGrid, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { GRID_BREAKPOINTS, GRID_COLS, GRID_MARGIN, GRID_ROW_HEIGHT } from "@/lib/layout-types";
import { WidgetCatalogDrawer } from "./WidgetCatalogDrawer";
import useGridTessellation from "@/hooks/useGridTessellation";
import useWidgetURLSync from "@/hooks/useWidgetURLSync";

const ResponsiveGrid = WidthProvider(Responsive);

function GmtClock() {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(id);
    }, []);
    return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground proportional-nums" data-testid="grid-gmt-clock">
            <Clock className="w-3 h-3" strokeWidth={1.75} />
            GMT+00 · {now.toISOString().slice(11, 16)}
        </span>
    );
}

function WidgetChrome({ widget, isMinimized, onMinimize, onClose }) {
    return (
        <div className="dgw-card bg-card border border-border rounded-xl shadow-sm" data-testid={`widget-${widget.id}`}>
            <div className="flex items-center h-10 shrink-0 px-2 border-b border-border/60">
                <div
                    className="widget-drag-handle flex items-center gap-1.5 flex-1 min-w-0 h-full px-1"
                    data-testid={`widget-drag-${widget.id}`}
                >
                    <GripVertical className="w-3.5 h-3.5 shrink-0 text-muted-foreground/70" strokeWidth={1.75} />
                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground truncate">
                        {widget.title}
                    </span>
                </div>
                <div className="dgw-actions flex items-center gap-0.5 shrink-0">
                    <button
                        type="button"
                        onClick={() => onMinimize(widget.id)}
                        title={isMinimized ? "Expand widget" : "Collapse widget"}
                        aria-label={isMinimized ? `Expand ${widget.title}` : `Collapse ${widget.title}`}
                        data-testid={`widget-minimize-${widget.id}`}
                        className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
                    >
                        {isMinimized ? <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> : <Minus className="w-3.5 h-3.5" strokeWidth={1.75} />}
                    </button>
                    <button
                        type="button"
                        onClick={() => onClose(widget.id)}
                        title="Remove widget (restore from Manage Widgets)"
                        aria-label={`Remove ${widget.title}`}
                        data-testid={`widget-close-${widget.id}`}
                        className="p-1 rounded-md text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                    >
                        <X className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </button>
                </div>
            </div>
            {!isMinimized && (
                <div className={`dgw-body ${widget.noPadding ? "" : "p-3 density-compact:p-2"}`}>
                    {widget.render()}
                </div>
            )}
        </div>
    );
}

export function DashboardGridWrapper({ role, viewId, widgets, className = "" }) {
    const {
        layouts, minimized, hidden,
        onLayoutChange, toggleMinimize, hideWidget,
        restoreWidget, restoreAll, resetLayout, dispatchResizeSoon,
    } = useDashboardLayout({ role, viewId, widgets });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { tessellate } = useGridTessellation(new Set(hidden));

    // URL↔layout sync: serialize layout to ?layout= for shareable URLs
    const currentBreakpointLayout = layouts?.lg || layouts?.md || [];
    const { writeLayoutToURL } = useWidgetURLSync({
        currentLayout: currentBreakpointLayout,
        onHydrateLayout: (hydratedLayout) => {
            // Convert hydrated array into the allLayouts shape and push into state
            const allLayouts = {};
            for (const bp of Object.keys(GRID_COLS)) {
                allLayouts[bp] = hydratedLayout.map((item) => ({
                    ...item,
                    w: Math.min(item.w, GRID_COLS[bp]),
                }));
            }
            onLayoutChange(null, allLayouts);
        },
        hiddenWidgetIds: new Set(hidden),
        viewId,
        enabled: true,
    });

    const visible = widgets.filter((w) => !hidden.includes(w.id));

    // Wrap onLayoutChange to apply tessellation (no URL write — that only fires on drag/resize stop)
    const handleLayoutChange = useCallback(
        (currentLayout, allLayouts) => {
            const tessellated = tessellate(currentLayout);
            onLayoutChange(tessellated, allLayouts);
        },
        [onLayoutChange, tessellate],
    );

    // Write layout to URL only on intentional user actions (drag/resize stop)
    const handleUserLayoutAction = useCallback(() => {
        dispatchResizeSoon();
        // Grab the current lg breakpoint layout and serialize to URL
        const lgLayout = layouts?.lg;
        if (lgLayout && lgLayout.length > 0) {
            writeLayoutToURL(lgLayout, { replace: true });
        }
    }, [dispatchResizeSoon, layouts?.lg, writeLayoutToURL]);

    return (
        <div className={`dgw-root ${className}`} data-testid={`dashboard-grid-wrapper-${viewId}`}>
            {/* Persistent toolbar — catalog trigger + GMT+00 indicator */}
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <GmtClock />
                <div className="flex items-center gap-1.5">
                    {hidden.length > 0 && (
                        <span className="text-[10px] font-semibold text-muted-foreground" data-testid="hidden-widget-count">
                            {hidden.length} hidden
                        </span>
                    )}
                    <Button
                        size="sm" variant="ghost"
                        className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setDrawerOpen(true)}
                        data-testid="layout-manage-widgets-btn"
                    >
                        <LayoutGrid className="w-3.5 h-3.5 mr-1" strokeWidth={1.75} /> Manage widgets
                    </Button>
                </div>
            </div>

            <ResponsiveGrid
                breakpoints={GRID_BREAKPOINTS}
                cols={GRID_COLS}
                layouts={layouts}
                rowHeight={GRID_ROW_HEIGHT}
                margin={GRID_MARGIN}
                containerPadding={[0, 0]}
                compactType="vertical"
                preventCollision={false}
                draggableHandle=".widget-drag-handle"
                draggableCancel=".dgw-actions"
                resizeHandles={["se", "e", "s"]}
                onLayoutChange={handleLayoutChange}
                onDragStop={handleUserLayoutAction}
                onResizeStop={handleUserLayoutAction}
                useCSSTransforms
            >
                {visible.map((w) => (
                    <div key={w.id} className={`dgw-item ${minimized.includes(w.id) ? "dgw-min" : ""}`} data-testid={`grid-item-${w.id}`}>
                        <WidgetChrome
                            widget={w}
                            isMinimized={minimized.includes(w.id)}
                            onMinimize={toggleMinimize}
                            onClose={hideWidget}
                        />
                    </div>
                ))}
            </ResponsiveGrid>

            <WidgetCatalogDrawer
                open={drawerOpen}
                onOpenChange={setDrawerOpen}
                widgets={widgets}
                hidden={hidden}
                minimized={minimized}
                onRestore={restoreWidget}
                onRestoreAll={restoreAll}
                onHide={hideWidget}
                onToggleMinimize={toggleMinimize}
                onReset={resetLayout}
            />
        </div>
    );
}

export default DashboardGridWrapper;
